// =========================================================
// PDP ギフトオプションカード v2 - jc-gift-option.js
//
// 機能:
//   1. 開閉トグル (Lucide chevron CSS rotation)
//   2. 熨斗 radio change ハンドラ (「その他から選ぶ」選択時に select 表示)
//   3. 熨斗 select change ハンドラ (select 値 → hidden input)
//   4. MC select change ハンドラ ("父の日(自由記入)" 選択時に input area 表示)
//   5. MC タブ切替 (定型文 / 自由入力)
//   6. 定型文 radio change ハンドラ
//   7. 自由入力 textarea (50字超過で .is-over warning class、ATC 干渉なし)
//   8. sessionStorage 状態保持 (Dawn variant change 後の再描画に対応)
//   9. MutationObserver で variant change 時の再初期化検知
//  10. 二重初期化防止 (data-jc-gift-init="1")
// =========================================================

(function() {
  'use strict';

  const STORAGE_KEY_PREFIX = 'jc-gift-v2-';

  function loadState(productId) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + productId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function saveState(productId, state) {
    try {
      sessionStorage.setItem(STORAGE_KEY_PREFIX + productId, JSON.stringify(state));
    } catch (e) { /* quota exceeded etc — silently ignore */ }
  }

  function initCard(card) {
    if (card.dataset.jcGiftInit === '1') return;
    card.dataset.jcGiftInit = '1';

    const productId = card.dataset.productId;

    // --- elements
    const header = card.querySelector('[data-toggle]');
    const toggleText = card.querySelector('[data-toggle-text]');
    const toggleIcon = card.querySelector('[data-toggle-icon]');
    const body = card.querySelector('.jc-gift-card__body');
    const subEl = card.querySelector('[data-state-sub]');

    const noshiRadios = card.querySelectorAll('[data-noshi-radio]');
    const noshiOtherRadio = card.querySelector('[data-noshi-other]');
    const noshiSelectWrap = card.querySelector('[data-noshi-select-wrap]');
    const noshiSelect = card.querySelector('[data-noshi-select]');
    const noshiNameWrap = card.querySelector('[data-noshi-name-wrap]');
    const noshiNameInput = card.querySelector('[data-noshi-name]');

    const mcSelect = card.querySelector('[data-mc-select]');
    const mcInputWrap = card.querySelector('[data-mc-input-wrap]');
    const mcTabs = card.querySelectorAll('.jc-gift-mc-tab');
    const mcPanelTemplate = card.querySelector('[data-panel="template"]');
    const mcPanelFree = card.querySelector('[data-panel="free"]');
    const mcTmplRadios = card.querySelectorAll('[data-tmpl]');
    const mcTextarea = card.querySelector('[data-mc-textarea]');
    const mcCounter = card.querySelector('[data-mc-counter]');
    const mcCounterWrap = mcCounter ? mcCounter.parentElement : null;

    const propNoshi = card.querySelector('[data-prop-out="noshi"]');
    const propMc = card.querySelector('[data-prop-out="mc"]');
    const propMcContent = card.querySelector('[data-prop-out="mcContent"]');
    const propNoshiName = card.querySelector('[data-prop-out="noshiName"]');

    // -------- state helpers --------
    function currentNoshiChoice() {
      const r = card.querySelector('[data-noshi-radio]:checked');
      return r ? r.value : '熨斗不要';
    }

    function currentMcMode() {
      const t = card.querySelector('.jc-gift-mc-tab.is-active');
      return t ? t.dataset.tab : 'template';
    }

    function currentMcTemplate() {
      const r = card.querySelector('[data-tmpl]:checked');
      return r ? r.value : '';
    }

    function currentState() {
      return {
        open: !body.hidden,
        noshiChoice: currentNoshiChoice(),
        noshiOther: noshiSelect ? noshiSelect.value : '',
        noshiName: noshiNameInput ? noshiNameInput.value : '',
        mcType: mcSelect ? mcSelect.value : 'メッセージカード不要',
        mcMode: currentMcMode(),
        mcTemplate: currentMcTemplate(),
        mcFree: mcTextarea ? mcTextarea.value : ''
      };
    }

    // -------- sync hidden inputs --------
    function syncProps() {
      const s = currentState();

      // 熨斗・水引の種類
      let noshiValue = '熨斗不要';
      if (s.noshiChoice === '__other') {
        noshiValue = s.noshiOther || '熨斗不要';
      } else if (s.noshiChoice) {
        noshiValue = s.noshiChoice;
      }
      if (propNoshi) propNoshi.value = noshiValue;

      // のし名入れ (select で熨斗を選択した時のみ有効。それ以外は値クリア = 矛盾防止)
      // 「父の日掛紙」「熨斗不要」は radio 側 (= noshiValue が '熨斗不要' or
      // featured radio の値) のため、select で実際に熨斗を選んだ場合のみ表示。
      const noshiNameVisible = (s.noshiChoice === '__other' && !!s.noshiOther);
      if (noshiNameWrap) noshiNameWrap.hidden = !noshiNameVisible;
      if (!noshiNameVisible) {
        if (noshiNameInput) noshiNameInput.value = '';
        s.noshiName = '';
      }
      if (propNoshiName) propNoshiName.value = noshiNameVisible ? s.noshiName : '';

      // メッセージカードの種類
      if (propMc) propMc.value = s.mcType || 'メッセージカード不要';

      // メッセージ内容
      let mcContent = '';
      if (s.mcType === '父の日(自由記入)') {
        if (s.mcMode === 'free') {
          mcContent = s.mcFree;
        } else {
          mcContent = s.mcTemplate;
        }
      }
      if (propMcContent) propMcContent.value = mcContent;

      // sub label
      updateSubLabel(s, noshiValue);

      saveState(productId, s);
    }

    function updateSubLabel(s, noshiValue) {
      if (!subEl) return;
      const noshiSelected = noshiValue && noshiValue !== '熨斗不要';
      const mcSelected = s.mcType && s.mcType !== 'メッセージカード不要';
      if (noshiSelected && mcSelected) {
        subEl.textContent = '熨斗・メッセージカード選択中';
      } else if (noshiSelected) {
        subEl.textContent = noshiValue.length > 16 ? noshiValue.slice(0, 16) + '…' : noshiValue;
      } else if (mcSelected) {
        subEl.textContent = s.mcType;
      } else {
        subEl.textContent = '熨斗・メッセージカード（無料）';
      }
    }

    // -------- restore --------
    function restore() {
      const s = loadState(productId);
      if (!s) {
        syncProps();
        return;
      }

      // open state
      if (s.open) {
        body.hidden = false;
        header.setAttribute('aria-expanded', 'true');
        if (toggleText) toggleText.textContent = '閉じる';
        if (toggleIcon) toggleIcon.classList.add('is-open');
      }

      // noshi
      if (s.noshiChoice) {
        const target = Array.from(noshiRadios).find(r => r.value === s.noshiChoice);
        if (target) target.checked = true;
      }
      if (s.noshiChoice === '__other') {
        if (noshiSelectWrap) noshiSelectWrap.hidden = false;
        if (noshiSelect && s.noshiOther) noshiSelect.value = s.noshiOther;
        // のし名入れ: 熨斗を選択済みなら欄を表示し値を復元 (syncProps が最終判定)
        if (s.noshiOther && noshiNameInput && s.noshiName) {
          noshiNameInput.value = s.noshiName;
        }
      }

      // mc
      if (mcSelect && s.mcType) {
        mcSelect.value = s.mcType;
      }
      if (s.mcType === '父の日(自由記入)') {
        if (mcInputWrap) mcInputWrap.hidden = false;
        if (s.mcMode === 'free') {
          switchMcTab('free', false);
        }
        if (s.mcTemplate) {
          const r = Array.from(mcTmplRadios).find(x => x.value === s.mcTemplate);
          if (r) r.checked = true;
        }
        if (mcTextarea && s.mcFree) {
          mcTextarea.value = s.mcFree;
          updateMcCounter();
        }
      }

      syncProps();
    }

    // -------- handlers --------
    function toggleBody() {
      const willOpen = body.hidden;
      body.hidden = !willOpen;
      header.setAttribute('aria-expanded', String(willOpen));
      if (toggleText) toggleText.textContent = willOpen ? '閉じる' : '開く';
      if (toggleIcon) toggleIcon.classList.toggle('is-open', willOpen);
      syncProps();
    }

    function onNoshiRadioChange() {
      const choice = currentNoshiChoice();
      if (noshiSelectWrap) {
        noshiSelectWrap.hidden = (choice !== '__other');
      }
      syncProps();
    }

    function onNoshiSelectChange() {
      syncProps();
    }

    function onMcSelectChange() {
      const selectedOption = mcSelect.options[mcSelect.selectedIndex];
      const hasMessage = selectedOption && selectedOption.dataset.message === 'true';
      if (mcInputWrap) mcInputWrap.hidden = !hasMessage;
      syncProps();
    }

    function switchMcTab(tabName, doSync) {
      mcTabs.forEach(t => {
        const active = t.dataset.tab === tabName;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
      });
      if (mcPanelTemplate) mcPanelTemplate.hidden = (tabName !== 'template');
      if (mcPanelFree) mcPanelFree.hidden = (tabName !== 'free');
      if (doSync !== false) syncProps();
    }

    function updateMcCounter() {
      if (!mcTextarea || !mcCounter) return;
      const len = (mcTextarea.value || '').length;
      mcCounter.textContent = len;
      if (mcCounterWrap) mcCounterWrap.classList.toggle('is-over', len > 50);
      syncProps();
    }

    // -------- bind --------
    header.addEventListener('click', toggleBody);
    noshiRadios.forEach(r => r.addEventListener('change', onNoshiRadioChange));
    if (noshiSelect) noshiSelect.addEventListener('change', onNoshiSelectChange);
    if (noshiNameInput) noshiNameInput.addEventListener('input', syncProps);
    if (mcSelect) mcSelect.addEventListener('change', onMcSelectChange);
    mcTabs.forEach(t => t.addEventListener('click', () => switchMcTab(t.dataset.tab, true)));
    mcTmplRadios.forEach(r => r.addEventListener('change', syncProps));
    if (mcTextarea) mcTextarea.addEventListener('input', updateMcCounter);

    // -------- initial --------
    restore();
  }

  function initAll() {
    document.querySelectorAll('[data-jc-gift-card]').forEach(initCard);
  }

  document.addEventListener('DOMContentLoaded', initAll);

  // Dawn variant change → product-info DOM 置換後、新しい snippet が現れる
  // MutationObserver で監視 (PubSub の variant:change 等は Dawn version 依存のため不採用)
  const obs = new MutationObserver(() => initAll());
  obs.observe(document.body, { childList: true, subtree: true });
})();
